import React, { useMemo } from 'react';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { FiltersState } from 'apps/sigma2-ui/src/lib/hooks/filter-state';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';

/**
 * Mostly copied from https://github.com/uidotdev/usehooks/blob/546df269e31a9ba10e0f6902fbda66d5eab7caa7/index.js#L595
 * Modified to add types
 */

type PrimitiveValue = string | boolean | number | null;

interface GenericObject {
  [key: string]:
    | PrimitiveValue
    | GenericObject
    | Array<PrimitiveValue>
    | FiltersState
    | Array<GenericObject>
    | null;
}

type SessionStorageValue = PrimitiveValue | GenericObject;

const dispatchStorageEvent = (
  key: string,
  newValue: string | null | undefined,
): void => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

const setSessionStorageItem = <T extends SessionStorageValue>(
  key: string,
  value: T,
): void => {
  const stringifiedValue = JSON.stringify(value);
  window.sessionStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeSessionStorageItem = (key: string) => {
  window.sessionStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getSessionStorageItem = (key: string) => {
  return window.sessionStorage.getItem(key);
};

const useSessionStorageSubscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const getSessionStorageServerSnapshot = () => {
  throw Error('useSessionStorage is a client-only hook');
};

type ValueFnSetState<T extends SessionStorageValue> = (
  value: (existingValue: T) => T,
) => void;

/**
 * Helper for working with session storage. Use it like you would with `useState` but it'll sync with the session storage
 * @example const [somevalue, setSomeValue] = useSessionStorage({ key: 'some key', defaultvalue: false })
 */
export function useSessionStorage<Value extends SessionStorageValue>({
  key,
  schema,
  defaultValue,
}: {
  key: string;
  schema: S2SafeParsable<unknown, Value>;
  defaultValue: Value;
}): [Value, ValueFnSetState<Value>] {
  const getSnapshot = () => getSessionStorageItem(key);

  const store = React.useSyncExternalStore(
    useSessionStorageSubscribe,
    getSnapshot,
    getSessionStorageServerSnapshot,
  );

  const storeValue = useMemo(() => {
    const parseResult = jsonParse(store ?? '', schema);

    if (parseResult.resultType === 'err') {
      return defaultValue;
    }

    return parseResult.value;
  }, [defaultValue, schema, store]);

  const setState = React.useCallback<ValueFnSetState<Value>>(
    (value) => {
      try {
        const nextState =
          typeof value === 'function' ? value(storeValue) : value;

        if (nextState === undefined || nextState === null) {
          removeSessionStorageItem(key);
        } else {
          setSessionStorageItem(key, nextState);
        }
      } catch (e) {
        uiAppLogger.warn(e);
      }
    },
    [key, storeValue],
  );

  React.useEffect(() => {
    if (
      getSessionStorageItem(key) === null &&
      typeof defaultValue !== 'undefined'
    ) {
      setSessionStorageItem(key, defaultValue);
    }
  }, [key, defaultValue]);

  return useMemo(() => {
    return [storeValue, setState];
  }, [setState, storeValue]);
}
